import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronsUpDown } from "lucide-react";
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@nexus/ui";

interface SaveFilterPresetControlProps {
  disabled?: boolean;
  presets: readonly { id: string; name: string }[];
  isPending?: boolean;
  i18nPrefix?: string;
  onSave: (name: string) => Promise<void>;
}

function isDuplicatePresetName(
  presets: readonly { name: string }[],
  name: string,
): boolean {
  const lower = name.trim().toLowerCase();
  if (!lower) return false;
  return presets.some((preset) => preset.name.toLowerCase() === lower);
}

export function SaveFilterPresetControl({
  disabled = false,
  presets,
  isPending = false,
  i18nPrefix = "drilldown.filterPresets",
  onSave,
}: SaveFilterPresetControlProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const duplicateMessageId = useId();

  const triggerLabel = t(`${i18nPrefix}.saveTrigger`, "Save Preset");
  const ariaLabel = t(`${i18nPrefix}.saveAria`, "Save Preset");
  const placeholder = t(`${i18nPrefix}.savePlaceholder`, "Preset name");
  const hint = t(`${i18nPrefix}.saveHintDedicated`, "Press Enter or click Save");
  const saveAction = t(`${i18nPrefix}.saveAction`, "Save");
  const disabledReason = t(
    `${i18nPrefix}.saveDisabledUnapplied`,
    "Apply changes before saving a preset",
  );
  const duplicateMessage = t(
    `${i18nPrefix}.saveDuplicateName`,
    "A preset with this name already exists",
  );

  const trimmed = name.trim();
  const isDuplicate = useMemo(
    () => isDuplicatePresetName(presets, trimmed),
    [presets, trimmed],
  );
  const canSubmit = Boolean(trimmed) && !isDuplicate && !isPending && !disabled;

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (disabled) return;
      setOpen(next);
    },
    [disabled],
  );

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    try {
      await onSave(trimmed);
    } catch {
      return;
    }
    setName("");
    setOpen(false);
  }, [canSubmit, onSave, trimmed]);

  return (
    <TooltipProvider delayDuration={0}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <Tooltip>
          <div className="inline-flex">
            <PopoverTrigger asChild>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={ariaLabel}
                  aria-expanded={open}
                  aria-disabled={disabled || undefined}
                  onClick={(event) => {
                    if (!disabled) return;
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onKeyDown={(event) => {
                    if (!disabled) return;
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                  }}
                  className={cn(
                    "inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-[10px] px-3 text-[13px] font-medium tracking-[-0.01em] outline-none transition-colors duration-150 select-none",
                    open
                      ? "bg-[var(--primary)]/10 text-[var(--primary-subtle-foreground)]"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    "focus-visible:ring-2 focus-visible:ring-ring",
                    disabled &&
                      "cursor-not-allowed opacity-60 hover:bg-transparent hover:text-muted-foreground",
                  )}
                >
                  <span className="shrink-0 font-normal">{triggerLabel}</span>
                  <ChevronsUpDown className="size-3.5 shrink-0 opacity-50" aria-hidden />
                </button>
              </TooltipTrigger>
            </PopoverTrigger>
          </div>
          {disabled ? <TooltipContent>{disabledReason}</TooltipContent> : null}
        </Tooltip>
        <PopoverContent
          align="start"
          className={cn(
            "w-72 overflow-hidden p-0 text-popover-foreground",
            "rounded-[12px] border border-border bg-popover",
            "shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)]",
          )}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <div className="px-3 py-2">
            <Input
              ref={inputRef}
              value={name}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== "Enter") return;
                event.preventDefault();
                void handleSubmit();
              }}
              placeholder={placeholder}
              aria-label={placeholder}
              aria-invalid={isDuplicate || undefined}
              aria-describedby={isDuplicate ? duplicateMessageId : undefined}
              disabled={isPending}
            />
            <p className="pt-1 text-xs text-muted-foreground">{hint}</p>
            {isDuplicate ? (
              <p
                id={duplicateMessageId}
                role="alert"
                className="pt-1 text-xs text-destructive"
              >
                {duplicateMessage}
              </p>
            ) : null}
            <Button
              type="button"
              size="sm"
              className="mt-2 w-full"
              disabled={!canSubmit}
              onClick={() => {
                void handleSubmit();
              }}
            >
              {saveAction}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  );
}
